import React, { useState, useEffect, useRef } from 'react';
import { Bell, BellRing, Check, Lock } from 'lucide-react';
import { SignInButton } from '@clerk/clerk-react';
import { useFollows } from '../hooks/useFollows';

/**
 * FollowButton -- in-app follow for signed-in Clerk users (Bell icon)
 *
 * Two variants via `compact` prop:
 * - Full (action rows): solid blue "Follow" button.
 *   Active state flips to outlined blue "Following" with check icon.
 * - Compact (list rows): small blue pill that says "Follow".
 *   Active state: blue-filled pill "Following".
 *
 * Anonymous users see a disabled-style button that opens the Clerk sign-in modal.
 * For email alerts (no account needed), use EmailAlertButton instead.
 */
const FollowButton = ({ targetType, targetId, label, compact = false, showTarget = false, iconOnly = false }) => {
  const { isFollowing, toggle, isSignedIn } = useFollows();
  const [toast, setToast] = useState(null); // 'followed' | 'unfollowed' | null
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);
  const promptRef = useRef(null);

  // Auto-dismiss toast after 2s
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 2000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Click outside to dismiss sign-in prompt
  useEffect(() => {
    if (!showSignInPrompt) return;
    const handleClickOutside = (e) => {
      if (promptRef.current && !promptRef.current.contains(e.target)) {
        setShowSignInPrompt(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSignInPrompt]);

  if (!targetType || !targetId) return null;

  const active = isSignedIn ? isFollowing(targetType, targetId) : false;

  const handleClick = (e) => {
    e.stopPropagation();
    if (!isSignedIn) {
      setShowSignInPrompt(true);
      return;
    }
    const wasActive = active;
    toggle(targetType, targetId);
    setToast(wasActive ? 'unfollowed' : 'followed');
  };

  // Sign-in prompt popover for anonymous users
  const SignInPrompt = ({ alignRight }) => (
    <div
      ref={promptRef}
      className={`absolute top-full mt-2 w-64 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 p-4 z-50 animate-fade-in ${
        alignRight ? 'right-0' : 'left-1/2 -translate-x-1/2'
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start gap-2.5 mb-3">
        <Bell size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-slate-700 dark:text-slate-300 leading-snug">
          Sign in to follow {label || 'this'} and get personalized deal alerts in your dashboard.
        </p>
      </div>
      <SignInButton mode="modal">
        <button className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
          Sign in to Follow
        </button>
      </SignInButton>
      {/* Small arrow pointing up */}
      <div className={`absolute -top-2 w-4 h-4 bg-white dark:bg-slate-800 border-l border-t border-slate-200 dark:border-slate-700 rotate-45 ${
        alignRight ? 'right-3' : 'left-1/2 -translate-x-1/2'
      }`} />
    </div>
  );

  /* -- Icon-only variant: tiny icon for inline use (e.g. feed card counterpart rows) -- */
  if (iconOnly) {
    return (
      <span className="relative inline-flex">
        <button
          onClick={handleClick}
          title={active ? `Unfollow ${label || ''}` : (isSignedIn ? `Follow ${label || ''}` : 'Sign in to follow')}
          className={`p-0.5 rounded transition-colors ${
            active
              ? 'text-blue-600 dark:text-blue-400'
              : isSignedIn
                ? 'text-slate-400 hover:text-blue-600 dark:text-slate-500 dark:hover:text-blue-400'
                : 'text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300'
          }`}
        >
          {active ? <BellRing size={13} /> : <Bell size={13} />}
        </button>
        {showSignInPrompt && <SignInPrompt alignRight />}
      </span>
    );
  }

  /* -- Compact variant: mini pill with text -- */
  if (compact) {
    return (
      <span className="relative inline-flex">
        <button
          onClick={handleClick}
          className={`px-2.5 py-1 rounded-lg text-xs font-medium flex items-center gap-1 transition-all ${
            active
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : isSignedIn
                ? 'text-[color:var(--gis-body)] bg-[color:var(--gis-rule)] hover:opacity-80'
                : 'text-slate-500 bg-slate-100 hover:bg-slate-200 dark:text-slate-400 dark:bg-slate-700 dark:hover:bg-slate-600'
          }`}
        >
          {active ? <Check size={12} /> : (isSignedIn ? <Bell size={12} /> : <Lock size={12} />)}
          {toast === 'followed'
            ? 'Alerts on!'
            : toast === 'unfollowed' ? 'Unfollowed' : active
              ? (showTarget && label ? `Following ${label}` : 'Following')
              : (showTarget && label ? `Follow ${label}` : 'Follow')}
        </button>
        {showSignInPrompt && <SignInPrompt alignRight />}
      </span>
    );
  }

  /* -- Full variant: solid blue CTA button -- */
  return (
    <span className="relative inline-flex">
      <button
        onClick={handleClick}
        className={`px-4 py-2 border rounded-xl text-sm font-medium flex items-center gap-2 transition-all ${
          active
            ? 'border-blue-300 text-blue-700 bg-white hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:bg-transparent dark:hover:bg-blue-900/20'
            : isSignedIn
              ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 shadow-sm'
              : 'bg-slate-100 text-slate-600 border-slate-300 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600 dark:hover:bg-slate-600'
        }`}
      >
        {toast === 'followed' ? <Check size={14} /> : active ? <BellRing size={14} /> : (isSignedIn ? <Bell size={14} /> : <Lock size={14} />)}
        {toast === 'followed'
          ? 'Alerts on!'
          : toast === 'unfollowed' ? 'Unfollowed' : active
            ? 'Following'
            : (isSignedIn ? 'Follow' : 'Sign in to Follow')}
      </button>
      {showSignInPrompt && <SignInPrompt />}
    </span>
  );
};

export default FollowButton;
